(() => {
  'use strict';
  // An element with id=WhoostrapCart also appears on window in browsers.
  // Use a separate controller name and check methods before reusing it.
  if (typeof window.WhoostrapCartAPI?.mount === 'function') {
    window.WhoostrapCartAPI.mount();
    return;
  }
  const originalFetch = window.fetch.bind(window);
  let dialog, root, opener, jobs = 0, queue = Promise.resolve(), refreshTimer, wantOpen = false;
  const status = (message = '', error = false) => {
    const el = dialog?.querySelector('[data-wc-status]');
    if (el) { el.textContent = message; el.classList.toggle('is-error', error); }
  };
  function lock() {
    const content = dialog?.querySelector('[data-wc-content]');
    if (content) { content.inert = jobs > 0; content.setAttribute('aria-busy', String(jobs > 0)); }
  }
  function run(action) {
    jobs++; lock(); status(dialog.dataset.loading);
    const job = queue.catch(() => {}).then(action);
    queue = job;
    return job.then(result => { status(); return result; }, error => {
      status(error.message || dialog.dataset.error, true); throw error;
    }).finally(() => { jobs--; lock(); });
  }
  async function request(url, options = {}) {
    const response = await originalFetch(url, { credentials: 'same-origin', ...options });
    if (!response.ok) {
      let body; try { body = await response.json(); } catch (_) {}
      throw new Error(typeof body?.description === 'string' ? body.description : dialog.dataset.error);
    }
    return response;
  }
  function updateCounter() {
    const count = Number(dialog.querySelector('[data-wc-content]').dataset.count);
    dialog.querySelector('[data-wc-count]').textContent = count;
    document.querySelectorAll('#cart-icon-bubble').forEach(icon => {
      const label = icon.querySelector('.covy-cart__label');
      const isCovy = icon.classList.contains('covy-cart') || !!label;
      const selector = isCovy ? '.covy-cart__bubble' : '.cart-count-bubble';
      // The custom header already has its own badge and responsive styling.
      if (isCovy) icon.querySelectorAll('.cart-count-bubble').forEach(extra => extra.remove());
      const bubbles = Array.from(icon.querySelectorAll(selector));
      let headerBadge = bubbles.shift();
      bubbles.forEach(extra => extra.remove());
      if (label) {
        const title = label.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
        label.textContent = `${title} (${count})`;
      }
      if (!count) { headerBadge?.remove(); return; }
      if (!headerBadge) {
        headerBadge = document.createElement('span');
        headerBadge.className = isCovy ? 'covy-cart__bubble' : 'cart-count-bubble';
        icon.append(headerBadge);
      }
      headerBadge.textContent = String(count);
    });
  }
  async function render() {
    const url = new URL(root, location.origin);
    url.searchParams.set('section_id', dialog.dataset.sectionId);
    const response = await request(url.href, { cache: 'no-store' });
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const next = page.querySelector('#WhoostrapCart [data-wc-content]');
    if (!next) throw new Error(dialog.dataset.error);
    const old = dialog.querySelector('[data-wc-content]');
    const scroll = old.querySelector('.wc__scroll').scrollTop;
    const focused = document.activeElement;
    const wasInside = old.contains(focused);
    const line = focused?.closest('[data-wc-line]');
    const lineIndex = line ? [...old.querySelectorAll('[data-wc-line]')].indexOf(line) : 0;
    const buttonIndex = line ? [...line.querySelectorAll('button')].indexOf(focused) : -1;
    old.replaceWith(next); lock(); updateCounter();
    next.querySelector('.wc__scroll').scrollTop = scroll;
    if (wasInside) {
      // Wait for the queue to release inert before restoring keyboard focus.
      queueMicrotask(() => setTimeout(() => {
        if (!dialog.open || jobs) return;
        const lines = next.querySelectorAll('[data-wc-line]');
        const row = lines[Math.min(lineIndex, lines.length - 1)];
        const target = row?.querySelectorAll('button')[Math.max(buttonIndex, 0)];
        (target && !target.disabled ? target : dialog.querySelector('[data-wc-close]')).focus({ preventScroll: true });
      }, 0));
    }
  }
  function close() { if (dialog?.open) dialog.close(); }
  function open(trigger) {
    if (!dialog) return Promise.reject(new Error('Cart unavailable'));
    if (!dialog.open) {
      opener = trigger instanceof HTMLElement ? trigger : document.activeElement;
      dialog.showModal(); document.documentElement.classList.add('wc-open');
    }
    return run(render);
  }
  function schedule(shouldOpen) {
    wantOpen = wantOpen || shouldOpen;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      const reveal = wantOpen; wantOpen = false;
      (reveal ? open() : run(render)).catch(() => {});
    }, 100);
  }
  function patchDawn() {
    ['cart-drawer', 'cart-notification'].forEach(tag => {
      document.querySelectorAll(tag).forEach(el => {
        el.open = trigger => { open(trigger).catch(() => {}); };
        el.close = close;
        el.renderContents = () => { schedule(true); };
      });
      customElements.whenDefined(tag).then(() => {
        document.querySelectorAll(tag).forEach(el => {
          el.open = trigger => { open(trigger).catch(() => {}); };
          el.close = close;
          el.renderContents = () => { schedule(true); };
        });
      });
    });
  }
  function mount() {
    const next = document.getElementById('WhoostrapCart');
    if (!next || typeof next.showModal !== 'function') return;
    if (next === dialog) { patchDawn(); return; }
    dialog = next;
    root = dialog.dataset.root || '/';
    if (!root.endsWith('/')) root += '/';
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('wc-open');
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
    dialog.addEventListener('click', event => {
      if (event.target === dialog) {
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right) close();
      }
    });
    patchDawn(); updateCounter();
    document.documentElement.classList.add('wc-ready');
  }
  document.addEventListener('click', event => {
    if (!dialog) return;
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    if (target.closest('[data-wc-close]')) { close(); return; }
    const button = target.closest('[data-wc-quantity]');
    if (button && dialog.contains(button)) {
      event.preventDefault();
      if (jobs || button.disabled) return;
      const key = button.closest('[data-wc-line]').dataset.wcLine;
      const quantity = Number(button.dataset.wcQuantity);
      run(async () => {
        await request(root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
        await render();
      }).catch(() => {});
      return;
    }
    const link = target.closest('a[href], [data-wc-open], #cart-icon-bubble');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
    const url = link.href ? new URL(link.href, location.origin) : null;
    const isCart = url && url.origin === location.origin && /\/cart\/?$/.test(url.pathname);
    if (isCart || link.matches('[data-wc-open], #cart-icon-bubble')) {
      event.preventDefault(); event.stopImmediatePropagation(); open(link).catch(() => {});
    }
  }, true);
  // Plain product forms: Dawn product-form and PDP bundle handlers retain ownership.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!dialog || !(form instanceof HTMLFormElement) || form.closest('product-form') || event.defaultPrevented) return;
    const action = new URL(form.action, location.origin);
    if (action.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
    const pdp = form.closest('[data-pdp]');
    if (pdp?.querySelector('[data-pdp-offer]')) return;
    if (pdp?.dataset.wcAppAtc === 'true') {
      try { if (document.querySelector(pdp.dataset.wcBundleSelector)) return; } catch (_) {}
    }
    event.preventDefault();
    const submit = event.submitter;
    if (submit?.disabled) return;
    const payload = new FormData(form);
    if (submit) submit.disabled = true;
    request(root + 'cart/add.js', { method: 'POST', body: payload })
      .then(() => open(submit)).catch(error => { open(submit).then(() => status(error.message, true)).catch(() => {}); })
      .finally(() => { if (submit) submit.disabled = false; });
  });
  // Observe successful AJAX cart changes without consuming or modifying their response.
  window.fetch = function(input, options) {
    const result = originalFetch(input, options);
    let url;
    try { url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.origin); } catch (_) { return result; }
    const match = url.origin === location.origin && url.pathname.match(/\/cart\/(add|change|update|clear)\.js$/);
    if (match) result.then(response => { if (response.ok && dialog) schedule(match[1] === 'add'); }, () => {});
    return result;
  };
  window.WhoostrapCartAPI = { open, close, refresh: () => run(render), mount };
  // Compatibility with the product sections already installed.
  window.WhoostrapCart = window.WhoostrapCartAPI;
  document.addEventListener('shopify:section:load', event => { mount(); if (event.target.contains(dialog)) open().catch(() => {}); });
  document.addEventListener('shopify:section:select', event => { if (event.target.contains(dialog)) open().catch(() => {}); });
  document.addEventListener('shopify:section:unload', event => { if (event.target.contains(dialog)) { close(); document.documentElement.classList.remove('wc-ready', 'wc-open'); } });
  document.addEventListener('cart:refresh', () => schedule(false));
  mount();
})();
